import moment from "moment";
/**
 * @classdesc Une activite d'un module
 * @kind class
 * @export
 * @prop {int} id - Id de l'activite
 *  @prop {int} id_module - Id du module
 * @prop {string} nom - Nom de l'activite
 *  @prop {int} prix - Prix de base de l'activite
 * @prop {boolean} obligatoire - L'activite est obligatoire
 * @prop {datetime} date_max - Date/Heure limite de disponibilité
 * @prop {boolean} visible_accueil - L'activite est visible sur l'accueil du module
 * @prop {int} quantite - Quantite de l'activite
 * @class Activite
 */
export default class Activite {
    id = "";
    id_module = "";
    nom = "";
    prix = "";
    obligatoire = 0;
    date_max = "";
    date_min = "";
    visible_accueil = 1;
    nb_inscrits = 0;
    quantite = 0;
    quantite_souhaite = 0;
    metadatas = [];


    /**
     * Crée une activite
     * @constructs
     * @kind function
     * @memberof Activite
     */
    constructor() { }

    /**
     * Défini une activite à partir d'un jeu de données
     *
     * @param {Object} data
     * @memberof Activite
     */
    set(data) {
        for (const [key, value] of Object.entries(data)) {
            if (this.hasOwnProperty(key)) {
                // Cloner les tableaux et objets pour éviter le partage de références
                if (Array.isArray(value)) {
                    this[key] = value.map(item => {
                        if (typeof item === 'object' && item !== null) {
                            return { ...item };
                        }
                        return item;
                    });
                } else if (typeof value === 'object' && value !== null) {
                    this[key] = { ...value };
                } else {
                    this[key] = value;
                }
            }
        }

    }

    /**
     * Met a jour le panier
     * @param {Object} vip_ins
     * @param {Object} lien_groupe
     * @returns {Object} Le panier
     * @memberof Activite
     */
    update_cart(vip_ins = null, lien_groupe = null) {
        let cart = { nb_articles: 0, prix: 0, prix_regulier: 0 };
        cart.nb_articles = parseInt(this.quantite_souhaite);
        const prix_normal = parseInt(this.quantite_souhaite) * parseFloat(this.prix);
        cart.prix = prix_normal;
        cart.prix_regulier = prix_normal;
        if (vip_ins && vip_ins.doit_payer_activite == '0') {
            cart.prix = 0;
        }

        if (lien_groupe && !lien_groupe.doit_payer_options) {
            cart.prix = 0;
        }
        this.current_price = cart.prix;
        this.cart = cart;
        return cart;
    }

    /**
     * Obtient les places disponibles pour cette activité
     * @param {Array} activites_coureur - La liste des selections de l'utilisateur courant pour les activités
     * @param {Array} list_inscriptions - La liste des inscriptions de la commande en cours
     * @param {Object} vip_ins - Est une inscription VIP
     * @param {boolean} on_add_activite_form - Est sur le formulaire d'ajout d'activité
     * @param {Array} list_commandes_options - La liste des options de la commande a laquelle on ajoute l'activité
     * @returns {number} Le nombre de places disponibles
     * @memberof Activite
     */



    get_places_disponibles(activites_coureur, list_inscriptions, vip_ins = null, on_add_activite_form = false, list_commandes_options = [], initial_commande_inscription, lien_groupe = null) {

        const DEFAULT_MAX = 30;
        let places_disponibles = {};
        if (vip_ins && vip_ins.acces_activites_completes == '1') {
            return DEFAULT_MAX;
        }

        if (lien_groupe && lien_groupe?.is_lien_groupe) {
            const inscriptions_options_completes = lien_groupe.inscriptions_options_completes;
            if (inscriptions_options_completes) {
                return DEFAULT_MAX;
            }
        }

        places_disponibles.default = DEFAULT_MAX; // Par défaut
        places_disponibles.max_reservable_item = this.get_max_reservable_item(); // Quantité maximale sélectionnable pour cette activité

        places_disponibles.max_reservable_item -= this.quantite_souhaite; // On retire la quantité déjà souhaitée
        if (on_add_activite_form) { // Si on est sur le formulaire d'ajout d'activité
            places_disponibles.max_reservable_item -= this.get_quantite_already_bought(list_commandes_options); // On retire la quantité déjà achetée
        }
        if (initial_commande_inscription) {
            places_disponibles.max_reservable_item -= this.get_quantite_already_bought(initial_commande_inscription.list_commandes_options); // On retire la quantité déjà achetée
        }
        places_disponibles.quantite_restante_item = this.get_quantite_item_disponible(); // Quantité restante pour cette activité
        places_disponibles.quantite_restante_item -= this.get_quantite_souhaite_on_commande(list_inscriptions, on_add_activite_form); // On retire la quantité déjà souhaitée sur la commande


        places_disponibles.max_reservable_group = this.get_max_reservable_group(); // Quantité maximale sélectionnable pour ce groupe d'activités
        places_disponibles.max_reservable_group -= this.get_quantite_souhaite_groupe(activites_coureur); // On retire la quantité déjà souhaitée
        if (on_add_activite_form) { // Si on est sur le formulaire d'ajout d'activité
            places_disponibles.max_reservable_group -= this.get_quantite_already_bought_group(list_commandes_options); // On retire la quantité déjà achetée
        }
        if (initial_commande_inscription) {
            places_disponibles.max_reservable_group -= this.get_quantite_already_bought_group(initial_commande_inscription.list_commandes_options); // On retire la quantité déjà achetée
        }

        places_disponibles.quantite_restante_group = this.get_quantite_group_disponible(activites_coureur); // Quantité restante pour ce groupe d'activités
        places_disponibles.quantite_restante_group -= this.get_quantite_souhaite_groupe_on_commande(list_inscriptions, on_add_activite_form); // On retire la quantité déjà souhaitée sur la commande
        let min_places_disponibles = Math.min(places_disponibles.default,
            places_disponibles.max_reservable_item,
            places_disponibles.quantite_restante_item,
            places_disponibles.max_reservable_group,
            places_disponibles.quantite_restante_group);

        min_places_disponibles = Math.max(min_places_disponibles, 0);
        return min_places_disponibles; // On retourne le minimum des places disponibles

    }


    /**
     * 
     * @param {*} activites_coureur - La liste des selections de l'utilisateur courant pour les activités
     * @returns {number} La quantité souhaitée pour ce groupe d'activités
     */
    get_quantite_souhaite_groupe(activites_coureur) {
        let quantite = 0;
        let current_group = this.get_metadata("nom_groupe_max_activite");
        if (!current_group) return 0;
        let activites_groupe = activites_coureur.filter((activite) => {
            return activite.get_metadata("nom_groupe_max_activite") == current_group;
        });
        for (let j = 0; j < activites_groupe.length; j++) {
            quantite += parseInt(activites_groupe[j].quantite_souhaite);
        }
        return quantite;
    }



    /**
     * Calculates the total quantity of desired activities in a specific group for a given list of inscriptions.
     * @param {Array} list_inscriptions - The list of inscriptions.
     * @param {boolean} [on_add_activite_form=false] - Indicates whether the function is called from the add activity form.
     * @returns {number} The total quantity of desired activities in the specified group.
     */
    get_quantite_souhaite_groupe_on_commande(list_inscriptions, on_add_activite_form = false) {
        let quantite = 0;
        let current_group = this.get_metadata("nom_groupe_max_activite");
        if (!current_group) return 0;
        for (let i = 0; i < list_inscriptions.length; i++) {
            let inscription = list_inscriptions[i];
            let activites_groupe = [];
            if (on_add_activite_form) {
                activites_groupe = inscription.list_activites_amended.filter((activite) => {
                    return activite.get_metadata("nom_groupe_max_activite") == current_group;
                });
            }
            else {
                activites_groupe = inscription.list_activites.list.filter((activite) => {
                    return activite.get_metadata("nom_groupe_max_activite") == current_group;
                });
            }


            for (let j = 0; j < activites_groupe.length; j++) {
                quantite += parseInt(activites_groupe[j].quantite_souhaite);
            }
        }
        return quantite;
    }


    /**
     * Calculates the total quantity of a specific activity on a command.
     * 
     * @param {Array} list_inscriptions - The list of inscriptions.
     * @param {boolean} [on_add_activite_form=false] - Indicates if the activity is being added on the form.
     * @returns {number} - The total quantity of the activity on the command.
     */
    get_quantite_souhaite_on_commande(list_inscriptions, on_add_activite_form = false) {
        let quantite = 0;
        for (let i = 0; i < list_inscriptions.length; i++) {
            let inscription = list_inscriptions[i];
            let activite = null;
            if (on_add_activite_form) {
                activite = inscription.list_activites_amended.find((activite) => {
                    return activite.id == this.id;
                });
            }
            else {
                activite = inscription.list_activites.list.find((activite) => {
                    return activite.id == this.id;
                });
            }
            if (activite) {
                quantite += parseInt(activite.quantite_souhaite);
            }
        }
        return quantite;
    }

    /**
     * Retrieves the maximum reservable item for this activity.
     * @returns {number|boolean} The maximum reservable item, or false if not found.
     */
    get_max_reservable_item() {
        for (let i = 0; i < this.metadatas.length; i++) {
            if (this.metadatas[i].nom == "quantite_max") {
                return parseInt(this.metadatas[i].valeur);
            }
        }
        return 30;
    }


    /**
     * Returns the quantity of available items for this activity.
     * @returns {number|boolean} The quantity of available items, or false if the quantity is zero.
     */
    get_quantite_item_disponible() {

        if (parseInt(this.quantite) == 0) return 30;
        let quantite_disponible = parseInt(this.quantite) - parseInt(this.nb_inscrits);
        return quantite_disponible < 0 ? 0 : quantite_disponible;
    }

    /**
     * Retrieves the maximum number of reservable groups for this activity.
     * @returns {number|boolean} The maximum number of reservable groups, or false if not found.
     */
    get_max_reservable_group() {
        for (let i = 0; i < this.metadatas.length; i++) {
            if (this.metadatas[i].nom == "quantite_max_groupe") {
                return parseInt(this.metadatas[i].valeur);
            }
        }
        return 30;
    }

    /**
     * Calculates the quantity of available group spots for the activity.
     * 
     * @param {Array} activites_coureur - The list of activities for the runner.
     * @returns {number} - The quantity of available group spots.
     */
    get_quantite_group_disponible(activites_coureur) {
        let places_reserves = 0;
        let current_group = this.get_metadata("nom_groupe_max_activite");
        let places_total_groupe = this.get_metadata("quantite_groupe");
        if (!places_total_groupe || !current_group) return 30;
        let activites_groupe = activites_coureur.filter((activite) => {
            return activite.get_metadata("nom_groupe_max_activite") == current_group;
        });
        for (let j = 0; j < activites_groupe.length; j++) {
            places_reserves += parseInt(activites_groupe[j].nb_inscrits);
        }
        let places_disponibles = places_total_groupe - places_reserves;
        return places_disponibles < 0 ? 0 : places_disponibles;

    }

    /**
     * Returns the quantity of this activity that has already been bought.
     * @param {Array} list_commandes_options - The list of command options.
     * @returns {number} - The quantity of this activity that has already been bought.
     */
    get_quantite_already_bought(list_commandes_options) {
        let quantite = 0;
        let activite_already_buy = list_commandes_options.find((activite) => {
            return activite.id_item == this.id && activite.type == "activite";
        });
        if (activite_already_buy) {
            quantite += parseInt(activite_already_buy.quantite);
        }
        return quantite;
    }

    /**
     * Calculates the total quantity of activities already bought for the current group.
     * 
     * @param {Array} list_commandes_options - The list of command options.
     * @returns {number} - The total quantity of activities already bought.
     */
    get_quantite_already_bought_group(list_commandes_options) {

        let quantite = 0;
        let current_group = this.get_metadata("nom_groupe_max_activite");
        let activites_already_buy = list_commandes_options.filter((activite) => {
            return activite.type == "activite" && activite.metadatas.nom_groupe_max_activite == current_group;
        });
        for (let j = 0; j < activites_already_buy.length; j++) {
            quantite += parseInt(activites_already_buy[j].quantite);
        }
        return quantite;
    }

    /**
      * Obtient la quantité maximale pour cette activité si elle existe, sinon retourne le max par défaut
      * @returns {number} La quantité maximale
      * @memberof Activite
      */
    get_max_quantite() {
        const DEFAULT_MAX = 30;
        let response = { value: DEFAULT_MAX, type: "item" };
        for (let i = 0; i < this.metadatas.length; i++) {
            if (this.metadatas[i].nom == "quantite_max") {
                response.value = parseInt(this.metadatas[i].valeur);
                response.type = "item";
                return response;

            }
        }
        return response;
    }


    get_max(vip_ins = null) {
        if (vip_ins) {
            if (vip_ins.acces_activites_completes == '1') {
                return this.get_max_quantite();
            }
        }
        let max = this.get_max_quantite();
        if (this.quantite != "0") {
            let restant = parseInt(this.quantite) - parseInt(this.nb_inscrits);
            if (restant < max) {
                max = Math.max(restant, 0);
            }
        }
        return max;
    }

    is_complet() {
        if (this.quantite == 0) return false;
        return parseInt(this.nb_inscrits) >= parseInt(this.quantite);
    }

    is_closed() {
        if (moment(this.date_max).isValid()) {
            let now = moment();
            let date_max = moment(this.date_max);
            if (now.isAfter(date_max)) {
                return true;
            }
        }
        if (moment(this.date_min).isValid()) {
            let now = moment();
            let date_min = moment(this.date_min);
            if (now.isBefore(date_min)) {
                return true;
            }
        }
        return false;


    }

    get_metadata(key) {
        for (let i = 0; i < this.metadatas.length; i++) {
            if (this.metadatas[i].nom == key) {
                return this.metadatas[i].valeur;
            }
        }
        return false;
    }

    get_text_prix() {
        if (parseInt(this.prix) == 0) {
            return "Gratuit";
        } else {
            return this.prix + "€";
        }
    }

    is_selectionnable(statut_ouverture_module, admin_view, admin_ins, vip_ins, lien_groupe = null) {
        if (lien_groupe && lien_groupe?.is_lien_groupe && (statut_ouverture_module == 'ouverte' || statut_ouverture_module == 'avant_ouverture')) {
            const is_complet = this.is_complet();
            const inscriptions_options_completes = lien_groupe.inscriptions_options_completes;
            if (inscriptions_options_completes) {
                return true;
            }
            return !is_complet;
        }
        if (!this.is_closed() && statut_ouverture_module == 'ouverte' && !this.is_complet()) {
            return true;
        }
        if (admin_view && !this.is_complet()) {
            return true;
        }
        if (admin_ins) {
            return true;
        }
        if (vip_ins && statut_ouverture_module == 'ouverte') {
            return vip_ins.acces_activites_completes == '1';
        }
        return false;
    }

    get_quantite_restante(all_activites) {
        if (this.quantite == 0) return -1;
        let quantite_restante = parseInt(this.quantite) - parseInt(this.nb_inscrits);

        let nom_groupe_max = this.get_metadata("nom_groupe_max_activite");
        if (nom_groupe_max) {
            let activites_in_groupe = all_activites.filter((activite) => {
                return activite.get_metadata("nom_groupe_max_activite") == nom_groupe_max;
            });
            for (let i = 0; i < activites_in_groupe.length; i++) {
                if (activites_in_groupe[i].id == this.id) continue;
                quantite_restante -= parseInt(activites_in_groupe[i].nb_inscrits);
            }
        }
        return quantite_restante < 0 ? 0 : quantite_restante;

    }

    getSimplifiedData() {
        if (this.quantite_souhaite == 0) return null;
        const infos = {};
        infos.prix = this?.cart?.prix;
        infos.quantite = this.quantite_souhaite;
        infos.id = this.id;
        infos.nom = this.nom;
        return infos;

    }

    is_quantite_editable() {
        if (this.metadatas.find(metadata => metadata.nom == "nom_groupe_max_activite" && metadata.valeur != '')) {
            return false;
        }
        return true;
    }


}
